import React, {useState} from 'react'
import {userActions} from '../../store/user/userActions'
import {userSelectors} from '../../store/user/userSelectors'
import styles from './BaseHeader.module.scss'
import {useNavigate} from 'react-router'
import {ReactComponent as QuestionProEXIcon} from '../../assets/Icons/EmployeeExp.svg'
import {ReactComponent as RightArrow} from '../../assets/Icons/RightArrow.svg'
import {ReactComponent as EmployeeIcon} from '../../assets/Icons/EmployeeIcon.svg'
import {ReactComponent as EmpowerIcon} from '../../assets/Icons/EmpowerIcon.svg'
import {ProductSwitcher} from './ProductSwitcher/ProductSwitcher'
import {UserBar} from './Userbar/UserBar'
import {DropdownComponent} from '../DropdownComponent'
import {initiativesActions} from '../../store/initiatives/initiativesActions'
import {initiativeAnalyticsActions} from '../../store/analyticsTeamView/analyticsTeamViewActions'
import {cx, getIsAdminUI, getWorkforceUrl, logout} from '../../utils'
import type {IWorkforce} from '../../types/workforceTypes'
import {BreadCrumbs} from './Breadcrumbs/Breadcrumbs'
import {WuAppHeader, WuSelect} from '@npm-questionpro/wick-ui-lib'
import {ideationApi} from '../../api/ideationApi'
import {useLocation} from 'react-router'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../../Translation/English/global'

export const BaseHeader = (): React.JSX.Element => {
  const navigate = useNavigate()
  const wt = useWt()
  const {search} = useLocation()
  const resetInitiativeAnalyticsAction =
    initiativeAnalyticsActions.useResetInitiativeAnalytics()
  const changeWorkforceNameAction = userActions.useChangeWorkforceName()
  const setSelectWorkforceAction = userActions.useSetSelectWorkforce()
  const abortRequestAction = initiativesActions.useAbortRequest()
  const resetInitiativesAction = initiativesActions.useResetInitiatives()
  const isAdminUI = getIsAdminUI()

  const selectedWorkforceName = userSelectors.useSelectedWorkforceName()
  const rawId = search ? search.substring(1) : ''
  const ideationId = !isAdminUI && rawId && !rawId.includes('=') ? rawId : ''
  const {data: ideation} = ideationApi.useIdeation(ideationId)
  const productSwitcher = userSelectors.useProductSwitcher()
  const accountDetails = userSelectors.useAccountDetails()
  const workforces = userSelectors.useWorkforces()
  // The product switcher currently has no open trigger (disabled in the
  // product-switcher revert); it stays closed until that feature returns.
  const [openSwitcher] = useState<boolean>(false)

  const goSelectWorkforce = (workforce: IWorkforce): void => {
    if (!workforce?.workforceID) return
    if (workforce.workforceName !== selectedWorkforceName) {
      abortRequestAction()
      resetInitiativeAnalyticsAction()
      resetInitiativesAction()
      changeWorkforceNameAction(workforce.workforceName)
      setSelectWorkforceAction(workforce)
      navigate({
        pathname: '/home',
      })
    }
  }

  return (
    <>
      {isAdminUI && (
        <WuAppHeader
          user={accountDetails}
          categories={productSwitcher}
          onLogout={() => logout()}
          productName={'Empower'}
        >
          <div className={styles.baseHeaderRow}>
            <div className="wu-flex wu-items-center wu-gap-3">
              {workforces.length > 0 && (
                <WuSelect
                  accessorKey={{
                    label: 'workforceName',
                    value: 'workforceName',
                  }}
                  data={workforces || []}
                  value={workforces.find(
                    wf => wf.workforceName === selectedWorkforceName,
                  )}
                  className={styles.baseHeaderSelect}
                  onSelect={(wf: IWorkforce | IWorkforce[]) => {
                    goSelectWorkforce(Array.isArray(wf) ? wf[0] : wf)
                  }}
                />
              )}
            </div>

            <div>
              <div className={styles.baseHeaderWrapperWorkforcesBlock}>
                <BreadCrumbs />
                <div className={styles.baseHeaderIdeationBlock}>
                  {ideation?.initiative?.name && (
                    <>
                      <RightArrow fill={'#ffffff'} />
                      <p className={styles.baseHeaderIdeationName}>
                        {ideation.initiative.name}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </WuAppHeader>
      )}

      {!isAdminUI && (
        <header
          className={cx(
            styles.baseHeader,
            styles.baseHeaderPortal,
            openSwitcher && styles.baseHeaderOpen,
          )}
        >
          <title>Empower: {selectedWorkforceName || ''}</title>
          <meta charSet="utf-8" />
          <div
            className={cx(
              styles.baseHeaderWrapper,
              styles.baseHeaderPortalWrapper,
            )}
          >
            <div className={styles.baseHeaderWrapperWorkforcesBlock}>
              <div className={styles.baseHeaderIdeationBlock}>
                {ideation?.initiative?.name && (
                  <>
                    <RightArrow fill={'#ffffff'} />
                    <p className={styles.baseHeaderIdeationName}>
                      {ideation.initiative.name}
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className={styles.baseHeaderWrapperActions}>
              <div
                className={cx(
                  styles.baseHeaderWrapperWorkforcesBlock,
                  styles.baseHeaderPortalWrapperWorkforcesBlock,
                )}
              >
                <DropdownComponent
                  value={
                    <div className={styles.baseHeaderSwitcher}>
                      <EmpowerIcon /> Empower
                    </div>
                  }
                  select={() => {}}
                  isBorderBottom={false}
                >
                  <div className={styles.baseHeaderSwitcher}>
                    <EmployeeIcon />
                    <a href={getWorkforceUrl()}>
                      {wt(GLOBAL.employeeExperience)}
                    </a>
                  </div>
                </DropdownComponent>
              </div>
              <UserBar />
            </div>
          </div>
          <div className={styles.baseHeaderLogoPortal}>
            <QuestionProEXIcon />
          </div>
          <ProductSwitcher isOpen={openSwitcher} />
        </header>
      )}
    </>
  )
}
